import React, { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface DateRangePickerProps {
    startDate?: string;
    endDate?: string;
    onChange: (start: string, end: string) => void;
    onClose: () => void;
}

const QUICK_RANGES = [
    { label: 'Сегодня', days: 0 },
    { label: 'Последние 3 дня', days: 3 },
    { label: 'Последние 14 дней', days: 14 },
    { label: 'Последние 60 дней', days: 60 },
] as const;

const toDateStr = (date: Date): string => date.toISOString().split('T')[0];

export const DateRangePicker: React.FC<DateRangePickerProps> = ({
    startDate,
    endDate,
    onChange,
    onClose,
}) => {
    const [start, setStart] = useState(startDate ?? '');
    const [end, setEnd] = useState(endDate ?? '');
    const [error, setError] = useState<string | null>(null);
    const ref = useRef<HTMLDivElement>(null);

    const maxDate = toDateStr(new Date());

    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) {
                onClose();
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [onClose]);

    useEffect(() => {
        const handler = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    }, [onClose]);

    const validate = useCallback(
        (s: string, e: string): string | null => {
            if (!s || !e) return 'Укажите обе даты';
            if (s > e) return 'Дата начала не может быть позже окончания';
            if (e > maxDate) return 'Дата окончания не может быть в будущем';
            const diffDays =
                (new Date(e).getTime() - new Date(s).getTime()) / 86_400_000;
            if (diffDays > 365) return 'Максимальный период — 365 дней';
            return null;
        },
        [maxDate],
    );

    const handleApply = () => {
        const err = validate(start, end);
        if (err) {
            setError(err);
            return;
        }
        onChange(start, end);
        onClose();
    };

    const applyQuickRange = (days: number) => {
        const now = new Date();
        const endStr = toDateStr(now);
        const startD = new Date(now);
        startD.setDate(startD.getDate() - days);
        setStart(toDateStr(startD));
        setEnd(endStr);
        setError(null);
    };

    const isValid = !validate(start, end);

    const durationDays =
        start && end && !error
            ? Math.round(
                (new Date(end).getTime() - new Date(start).getTime()) / 86_400_000,
                )
            : null;

    return (
        <motion.div
            ref={ref}
            initial={{ opacity: 0, y: 6, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 6, scale: 0.97 }}
            transition={{ duration: 0.15 }}
            className="absolute top-full left-0 mt-1.5 bg-[var(--bg-elevated)] border border-[var(--border-strong)] rounded-xl shadow-lg p-4 z-50"
            style={{ minWidth: 290 }}
            role="dialog"
            aria-modal="true"
            aria-label="Выбор периода дат"
        >
            <p className="text-[10px] font-medium text-[var(--text-muted)] uppercase tracking-[0.06em] mb-2">
                Быстрый выбор
            </p>
            <div className="flex flex-wrap gap-1.5 mb-3">
                {QUICK_RANGES.map(({ label, days }) => (
                <button
                    key={label}
                    onClick={() => applyQuickRange(days)}
                    className="px-2.5 py-1 rounded-lg bg-[var(--bg)] border border-[var(--border)] text-[11px] text-[var(--text-secondary)] hover:border-[var(--brand-border)] hover:text-[var(--brand)] transition-all"
                >
                    {label}
                </button>
                ))}
            </div>

            <div className="h-px bg-[var(--border)] mb-3" />

            {/* Date inputs */}
            <div className="grid grid-cols-2 gap-2 mb-2">
                <div>
                    <label className="block text-[10px] text-[var(--text-muted)] mb-1.5 font-medium">
                        Начало
                    </label>
                    <input
                        type="date"
                        value={start}
                        max={end || maxDate}
                        onChange={e => { setStart(e.target.value); setError(null); }}
                        className="w-full px-2.5 py-2 rounded-lg bg-[var(--bg)] border border-[var(--border)] text-[var(--text)] text-[12px] focus:outline-none focus:border-[var(--brand)] transition-colors"
                    />
                </div>
                <div>
                    <label className="block text-[10px] text-[var(--text-muted)] mb-1.5 font-medium">
                        Конец
                    </label>
                    <input
                        type="date"
                        value={end}
                        min={start}
                        max={maxDate}
                        onChange={e => { setEnd(e.target.value); setError(null); }}
                        className="w-full px-2.5 py-2 rounded-lg bg-[var(--bg)] border border-[var(--border)] text-[var(--text)] text-[12px] focus:outline-none focus:border-[var(--brand)] transition-colors"
                    />
                </div>
            </div>

            <AnimatePresence>
                {durationDays !== null && !error && (
                    <motion.p
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="text-[11px] text-[var(--text-muted)] mb-2"
                    >
                        Период: {durationDays} {durationDays === 1 ? 'день' : durationDays < 5 ? 'дня' : 'дней'}
                    </motion.p>
                )}
            </AnimatePresence>

            <AnimatePresence>
                {error && (
                    <motion.p
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="text-[11px] text-[var(--status-down-text)] mb-2"
                    >
                        {error}
                    </motion.p>
                )}
            </AnimatePresence>

            <div className="flex gap-2 mt-2">
                <button
                    onClick={onClose}
                    className="flex-1 py-2 rounded-lg border border-[var(--border)] text-[var(--text-secondary)] text-[12px] font-medium hover:bg-[var(--bg-hover)] transition-colors"
                >
                    Отмена
                </button>
                <button
                    onClick={handleApply}
                    disabled={!isValid}
                    className="flex-1 py-2 rounded-lg bg-[var(--brand)] text-white text-[12px] font-medium hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed"
                >
                    Применить
                </button>
            </div>
        </motion.div>
    );
};